import { makeAutoObservable } from 'mobx';
import { IRootStore, ISpeechRecognitionStore, ITextEntity } from '@shared/types';
import { IWordCompareResult, WordStatus } from '@shared/types';
import { MicrophoneService } from '../../services/core';
import { readingSessionLogger } from '../../services/analytics/reading-session-logger.service';
import { isCoarticulated, readerPace } from '../../utils/coarticulation';
import { verdictFor } from '../../utils/word-distance';
import { incrementalAlign, IncrementalAlignResult, WordTiming } from './compare-functions';
import {
  DIVINE_NAME_WRITTEN_FORMS,
  readingsForBareWord,
} from '../../config/dictionary.config';
import { removeNonLetters, removeVowelPoints } from '../../utils/text-cleaning.utils';

import { io, Socket } from 'socket.io-client';
import ReadingSessionStore from '../data/reading-session.store';
import { ClientToServerEvents, getSocketServerUrl, ServerToClientEvents } from './socket-connection';
import { buildLogBackendFinish, buildLogBatchRecognitionStart, buildLogEndConditionStats, buildLogEndSession, buildLogEvalWord, buildLogFrontendFinish, buildLogStartRecording, buildLogStartTiming, buildLogTranscription, buildLogVoiceLevel } from './log-building';
import { VoiceLevelController } from './voice-level.controller';
import { fetchPleneText } from '@web/services/api/plene.service';
import { none } from 'node_modules/animejs/dist/modules/easings/none';
 
/**
 * 🎙️ SPEECH RECOGNITION CONTROLLER - Controller ראשי לזיהוי דיבור
 * 
 * **תפקיד ראשי:** Controller שמנהל את כל תהליך הזיהוי קולי והאינטראקציה עם המשתמש
 * 
 * **אחריות:**
 * - ניהול Web Speech Recognition API (הפעלה, עצירה, התחדשות)
 * - ניהול מצבי ממשק המשתמש (isListening, isPaused, isStarted, isDone)
 * - עיבוד קלט קולי וזיהוי מילים (processWordRecognition)
 * - ממשק אחיד לכפתורי פעולה (removeLastWord, removeAllText, toggleListening)
 * - תיאום עם readingSessionStore לעדכון נתוני הקריאה
 * - טיפול בשגיאות ומצבי edge cases (אובדן פוקוס, שגיאות רשת)
 * 
 * **מתקשר עם:**
 * - readingSessionStore (פנימית) - לעדכון נתוני הקריאה
 * - UI Components - ממשק חיצוני לכפתורים ותצוגה
 * - userStore - לקבלת הגדרות דיוק קולי
 * - uiStore - לפתיחת דיאלוגים
 * 
 * **Pattern:** Facade + Controller - מסתיר מורכבות פנימית ומספק ממשק פשוט
 */
class SpeechRecognitionController implements ISpeechRecognitionStore {
  // קונסטנטות
  private static readonly RESTART_DELAY = 100;
  private static readonly DELETE_RESTART_DELAY = 200;

  // משתנים עיקריים
  rootStore: IRootStore;
  // recognition: any = null;
  // Legacy variable - משמש כ-fallback ב-currentSpokenText אם אין session פעיל
  // TODO: לבדוק אם ה-fallback באמת נחוץ בעתיד
  speechText = ''; // נשמר לתאימות לאחור - משמש fallback ב-currentSpokenText
  isListening = false;
  isStarted = false;
  isInitializing = false;
  isPaused = false;
  isDone = false;
  score = 0;

  // Legacy variables - נשמרים לתאימות עם ISpeechRecognitionStore interface
  // TODO: לבדוק אם אפשר להסיר בעתיד
  allSpeechTexts = ''; // לא בשימוש פעיל - נשמר לתאימות
  readingResults: any; // לא בשימוש - נשמר לתאימות
  lastProcessedWord = '';

  // משתנים לניהול זמן שגיאות
  private lastErrorTime = 0;
  private static readonly ERROR_DEBOUNCE_TIME = 500; // חצי שניה במילישניות

  // דגל למניעת הפעלה מחדש במהלך פעולות מחיקה
  private isPerformingDeleteOperation = false;

  // ניטור מצב הטאב
  private pageVisibilityHandler!: () => void;

  // מזהה עולה לכל אתחול הקלטה, כדי שאתחול ישן לא ידרוס מצב של אתחול חדש
  private recognitionGeneration = 0;

  // מתי השמע התחיל לעבור לתמלול. זה גם אפס של זמני המילים שהשרת מחזיר
  private listeningStartedAt = 0;

  // שער השמע לתמלול. כל עוד הוא סגור המיקרופון פועל, אבל שום צליל לא נשלח
  // לזיהוי: לא רעש רקע, לא דיבור של מי שעוד לא התכוון להתחיל. הוא נפתח רק
  // כשהשרת מאשר שהזיהוי באמת מקשיב, ואז גם הכיסוי מעל הפסוק נעלם.
  private forwardAudio = false;

  // האישור מהשרת שהזיהוי מקשיב (Speechmatics שלח RecognitionStarted)
  private streamReadyPromise: Promise<void> | null = null;
  private streamReadyResolve: (() => void) | null = null;
  /** Recogniser setting from the server's last streamReady; 'unknown' from a server that doesn't send it. */
  private operatingPoint = 'unknown';
  // רשת ביטחון: אם האישור לא מגיע, לא משאירים את התלמיד מול כיסוי לנצח
  private static readonly STREAM_READY_TIMEOUT = 3000;

  // מספר הקריאה ברצף מאז שהמיקרופון נפתח: 1 = פתיחה קרה, 2 ומעלה = פסוק נוסף בזרימה
  private flowPosition = 0;

  // פתיחה מוקדמת: כשהפסוק מוצג, לפני הכפתור הירוק, המיקרופון והזיהוי כבר נפתחים
  // (השער סגור, שום צליל לא נשלח). הלחיצה "לוקחת" את הסשן המוכן, ואם אף אחד
  // לא לוחץ תוך 30 שניות הכל נסגר.
  private preparedStream = false;
  private preparePromise: Promise<void> | null = null;
  private prepareGeneration = 0;
  private prepareIdleTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private static readonly PREPARE_IDLE_MS = 30_000;

  // מדידת ההתחלה של הקריאה הנוכחית, נרשמת ב-ReadingLogs כשהשער נפתח
  private startTiming: {
    startedAt: number;
    path: 'cold' | 'warm';
    micReadyAt: number;
    readyAt: number | null;
    readyTimedOut: boolean;
    preopened: boolean;
  } | null = null;

  // ניהול timeouts למניעת memory leaks
  private restartTimeoutId: NodeJS.Timeout | null = null;
  private deleteRestartTimeoutId: NodeJS.Timeout | null = null;

  // ניטור מצב המיקרופון
  private microphoneCheckInterval: NodeJS.Timeout | null = null;
  private lastRecognitionActivity: number = 0;
  private recognitionStartTime: number = 0; // זמן התחלת recognition - לזיהוי כיבוי מהיר במובייל
  private static readonly MICROPHONE_CHECK_INTERVAL = 2000; // בדיקה כל 2 שניות
  private static readonly MOBILE_AUTO_OFF_THRESHOLD = 2000; // 2 שניות - במובייל הדפדפן כבה מהר מאוד
  private static readonly MOBILE_QUICK_OFF_THRESHOLD = 1500; // 1.5 שניות - כיבוי מהיר במובייל (כנראה אוטומטי)

  private transcribedWords: Array<string> = [];
  private wordScores: Array<number> = [];
  /**
   * Armed once the reader is within a word of the end. If no further final
   * segment arrives in this time the reading is closed as it stands. Without
   * it a last word the recogniser drops or mishears leaves the reading open
   * for ever: no score, no celebration, no flow, and only ביטול gets out.
   */
  private static readonly TAIL_IDLE_MS = 3500;
  private tailIdleTimeoutId: NodeJS.Timeout | null = null;
  /**
   * The words the tail timer was armed against, so a partial can restart it.
   * A partial means the recogniser is still hearing speech, so the tail is not
   * quiet yet; only silence should close the reading.
   */
  private tailIdleWords: string[] | null = null;
  /** Timing of each matched word so far, by text index; null where unmatched. */
  private wordTimings: Array<WordTiming | null> = [];
  /** Correctly read words with timings, the sample the reader's pace is taken from. */
  private paceSamples: Array<{ word: string; timing: WordTiming }> = [];
  private logEntries: Array<any> = [];

  /**
   * The reader's loudness: meter and speak-up bubble on screen (behind a
   * switch) and one READING_VOICE_LEVEL row per stream. It only listens to
   * the microphone graph; it never changes what is sent or how it is scored.
   */
  readonly voiceLevel = new VoiceLevelController();

  // Incremental aligner cursor — index into correctWords up to which finals have been aligned
  private alignCursor = 0;
  // In-progress partial text shown in UI while the user is mid-word (no scoring)
  partialText = '';

  private socket: Socket<ServerToClientEvents, ClientToServerEvents> = io(getSocketServerUrl(), {
    autoConnect: true,
  });

  constructor(rootStore: IRootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    this.setupPageVisibilityHandler();
    // קריאה async ללא await - תרוץ ברקע
    this.setupMicrophoneMonitoring().catch((error) => {
      console.warn('Error setting up microphone monitoring:', error);
    });

    this.socket.on('transcript', async (tObject: any) => {
      if (tObject["type"] === "partial") {
        // Update display only — no alignment computation on partials
        this.partialText = tObject["text"] as string;
        // Speech is still coming in: the tail is not quiet. Restart the clock
        // so a slow last word is not cut off while the recogniser is mid-guess.
        if (this.tailIdleTimeoutId && this.tailIdleWords && this.partialText.trim().length > 0) {
          this.armTailIdle(this.tailIdleWords);
        }
        return;
      }

      if (tObject["type"] === "final") {
        this.partialText = '';
        const heardWords: string[] = (tObject["text"] as string)
          .trim()
          .split(/\s+/)
          .filter((w: string) => w.length > 0);

        if (heardWords.length === 0) return;

        // Word details from speechmatics: per-word when the payload count matches the
        // split words, otherwise fall back to the segment window (or null if unavailable).
        // The confidence only exists per-word, so the fallback leaves it out rather
        // than inventing one for the whole segment.
        const rawTimes = (tObject["times"] as Array<{ start_time: number; end_time: number; confidence?: number }> | undefined) ?? [];
        const segStart = tObject["start_time"];
        const segEnd = tObject["end_time"];
        const segmentTiming =
          typeof segStart === 'number' && typeof segEnd === 'number'
            ? { start_time: segStart, end_time: segEnd }
            : null;
        const reliable = rawTimes.length === heardWords.length;
        const heardWordTimes: Array<WordTiming | null> = heardWords.map((_, i) => {
          if (reliable && rawTimes[i]) {
            return {
              start_time: rawTimes[i].start_time,
              end_time: rawTimes[i].end_time,
              confidence: rawTimes[i].confidence,
            };
          }
          return segmentTiming;
        });

        const finalWords = heardWords;
        const finalWordTimes = heardWordTimes;

        let cWords = await this.correctWords();
        // The pointed words on screen, one for one with the text being scored, so
        // the dots can decide the sound-alike letters (כּ/כ, שׂ/שׁ, בּ/ב).
        const shown = this.readingSessionStore.allWords;
        const pointed = shown.length === cWords.length ? shown.map((word: { cleanWord: string }) => word.cleanWord) : undefined;
        const [result, correctWords] = SpeechRecognitionController.customResultDivineName(
          cWords, finalWords, this.alignCursor, finalWordTimes, pointed,
        );
        if (result.newCursor > this.alignCursor) {
          this.applySegmentToSession(correctWords, this.alignCursor, result);
          // Extend wordScores for the covered range
          for (let i = 0; i < result.scores.length; i++) {
            this.wordScores[this.alignCursor + i] = result.scores[i];
          }
          this.alignCursor = result.newCursor;
          this.transcribedWords = [...this.transcribedWords, ...finalWords];
          this.checkDoneCondition(correctWords);
        }
      }
    });

    this.socket.on('streamReady', (info?: { operating_point?: string }) => {
      this.operatingPoint = info?.operating_point ?? 'unknown';
      this.streamReadyResolve?.();
      this.streamReadyResolve = null;
    });

    // סשן שנפתח מראש מת עם החיבור לשרת. בלי זה הלחיצה הייתה לוקחת סשן שכבר
    // לא קיים, והשמע היה הולך לאיבוד. קריאה שכבר רצה לא משתנה כאן.
    this.socket.on('disconnect', () => {
      if (!this.preparedStream) return;
      this.preparedStream = false;
      this.streamActive = false;
      this.streamReadyPromise = null;
      this.streamReadyResolve = null;
    });

    this.socket.on('transcriptError', () => {
      this.logEntries.push(buildLogBackendFinish());
      this.doneListening();
    });
  }

  private correctWordsPromise: Promise<string[]> | null = null;

  private async correctWords(): Promise<string[]> {
    if (this.correctWordsPromise === null) {
      this.correctWordsPromise = this.computeCorrectWords();
    }
    return this.correctWordsPromise;
  }

  private async computeCorrectWords(): Promise<string[]> {
    let eID = this.readingSessionStore.readingSession.textEntityId
    let pleneText = await fetchPleneText("Sessions", eID);
    let pleneArray = pleneText.text
      .replace(/[\p{P}\p{S}]/gu, "") // remove all Unicode punctuation & symbols
      .split(/\s+/)
      .filter(word => word.length > 0);
    if (pleneArray.length > 0) {
      return pleneArray
    }
    var correct = []
    for (let iWord of this.readingSessionStore.allWords) {
      correct.push(iWord.cleanWord);
    }
    return correct
  }

  // ---- Getters למטמון קריאות ל-stores ----
  private get readingSessionStore() {
    return this.rootStore.stores.readingSessionStore;
  }

  private get booksStore() {
    return this.rootStore.stores.booksStore;
  }

  private get userStore() {
    return this.rootStore.stores.userStore;
  }

  private get uiStore() {
    return this.rootStore.stores.uiStore;
  }

  private get siddurStore() {
    return this.rootStore.stores.siddurStore;
  }

  /**
   * The text this session is reading against.
   *
   * There is one pipeline, not two. The Siddur reader hands it the same shape
   * the book reader does, so everything downstream (word alignment, statuses,
   * the done condition) is unchanged and cannot drift between the two readers.
   */
  get readingSource(): ITextEntity | null {
    if (this.siddurStore?.isReading) {
      return this.siddurStore.currentScreenReading?.entity ?? null;
    }
    return this.booksStore.selectedTextEntity ?? null;
  }

  get cleanSelectedTextEntityArray(): string[] {
    return this.readingSource?.cleanWordsArray || [];
  }

  get userTextToDisplay(): IWordCompareResult[] {
    return this.fullTextWithStatuses;
  }

  get accuracyScore(): number {
    return this.readingSessionStore.stats.accuracyPercentage;
  }

  get isActuallyListening(): boolean {
    return this.isListening;
  }

  get getErrorAttemptId(): number {
    return this.readingSessionStore.readingSession?.errorAttemptId || 0;
  }

  get fullTextWithStatuses(): IWordCompareResult[] {
    if (!this.readingSessionStore.hasActiveSession) {
      // חזרה לממשק הישן אם אין session פעיל
      return [];
    }

    return this.readingSessionStore.allWords.map((word: any) => ({
      wordToDisplay:
        word.isCurrentWord && word.status === WordStatus.Mistake
          ? this.readingSessionStore.readingSession?.currentAttemptedWord || word.cleanWord1``
          : word.cleanWord,
      wordStatus: word.status,
    }));
  }

  // גטר מעודכן לטקסט שנאמר - תמיד מ-ReadingSession אם קיים
  get currentSpokenText(): string {
    return this.readingSessionStore.hasActiveSession ? this.readingSessionStore.spokenText : this.speechText;
  }

  /**
   * Aligns a segment, accepting any spoken reading of a word that is written
   * one way and said another: the Divine Name above all.
   *
   * The written text is returned unchanged apart from normalising the Name's
   * several pointings to one form, so what gets logged and scored is the word
   * the child actually saw, not the substitution we matched it by.
   */
  private static customResultDivineName(
    correct: string[],
    finalWords: string[],
    cursor: number,
    finalWordTimes?: (WordTiming | null)[],
    pointed?: string[],
  ): [IncrementalAlignResult, string[]] {
    const CANONICAL_NAME = 'יהוה';

    const pointedForms = DIVINE_NAME_WRITTEN_FORMS.filter(
      (form) => removeNonLetters(removeVowelPoints(form)) === CANONICAL_NAME,
    );
    const escaped = pointedForms.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const namePattern = escaped.length ? new RegExp(escaped.join('|'), 'g') : null;

    // Collapse the pointings to one written form. Read against the text, not a
    // rewrite of it, so the logs record what the child was actually shown.
    const normalisedCorrect = namePattern
      ? correct.map((word) => word.replace(namePattern, CANONICAL_NAME))
      : [...correct];

    // One rule, in the dictionary, so the reader and its tests ask the same
    // question. It knows the Name with a prefix as well as without.
    const bareCorrect = normalisedCorrect.map((word) => removeNonLetters(removeVowelPoints(word)));
    const equivalents: (string[] | undefined)[] = bareCorrect.map((bare, i) =>
      readingsForBareWord(bare, i > 0 ? bareCorrect[i - 1] : undefined),
    );

    const result = incrementalAlign(
      normalisedCorrect,
      finalWords,
      cursor,
      finalWordTimes,
      equivalents,
      pointed,
    );
    return [result, normalisedCorrect];
  }

  // ---- פונקציות פרטיות ----

  /**
   * Helper function לבדיקה אם אנחנו במובייל
   */
  private static isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  private recognitionAbort(): void {
    this.forwardAudio = false;
    this.voiceLevel.pause();
    this.logEntries.push(buildLogFrontendFinish());
    this.input?.disconnect()
  }

  private handleCriticalError(): void {
    console.warn('🚨 טיפול בשגיאה קריטית - מבצע איפוס מלא של המערכת');

    // 📊 Log session as crashed
    if (readingSessionLogger.isSessionActive()) {
      this.logSessionEnd('crashed', 'system_error');
    }

    // איפוס מלא של כל המצבים
    this.setIsListening(false);
    this.setIsPaused(false);
    this.setIsStarted(false);

    // כיבוי המיקרופון
    if (this.recognition) {
      // this.recognition.onend = null; // מניעת הפעלה מחדש אוטומטית
      this.recognitionAbort();
    }

    // וידוא שהממשק חוזר למצב התחלתי
    this.lastProcessedWord = '';
  }

  private setupPageVisibilityHandler(): void {
    this.pageVisibilityHandler = () => {
      if (document.hidden && this.isListening) {
        // כשעוברים לשונית אחרת - המיקרופון נכבה
        this.stopListeningDueToVisibilityChange();
      } else if (document.hidden && this.preparedStream && !this.isInitializing) {
        // נפתח מראש ואף אחד לא קורא: לא משאירים מיקרופון וסשן פתוחים מאחורי לשונית אחרת
        this.releaseMicrophone().catch((error) => {
          console.warn('Error releasing a prepared microphone:', error);
        });
      } else if (!document.hidden && this.isPaused && this.isStarted && !this.isDone) {
        // כשחוזרים לשונית - בודקים אם המיקרופון נכבה ומציגים פופאפ
        this.checkMicrophoneStatusAfterReturn();
      }
    };

    document.addEventListener('visibilitychange', this.pageVisibilityHandler);
  }

  private stopListeningDueToVisibilityChange(): void {
    // 📊 Log session end if active (page hidden)
    if (readingSessionLogger.isSessionActive()) {
      this.logSessionEnd('abandoned', 'user_left');
    }

    this.setIsListening(false);
    this.setIsPaused(true);
    if (this.recognition) {
      this.recognitionAbort();
    }

    // הצגת פופאפ כשעוברים לשונית - המיקרופון נכבה
    this.handleMicrophoneTurnedOff('tab-switch');
  }

  /**
   * בודק את מצב המיקרופון כשחוזרים לשונית
   * אם המיקרופון נכבה - מציג פופאפ
   */
  private async checkMicrophoneStatusAfterReturn(): Promise<void> {
    // בדיקה אם יש הרשאה למיקרופון
    if ('permissions' in navigator) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });

        // אם ההרשאה נדחתה - המיקרופון נכבה
        if (permissionStatus.state === 'denied') {
          console.warn('🔇 Microphone permission denied after returning to tab');
          this.handleMicrophoneTurnedOff('permission-denied');
          return;
        }

        // אם ההרשאה granted אבל המיקרופון עדיין לא פעיל - נציג פופאפ
        // (המיקרופון נכבה כשעברו לשונית)
        if (permissionStatus.state === 'granted' && this.isPaused && !this.isListening) {
          console.warn('🔇 Microphone was turned off when switching tabs');
          this.handleMicrophoneTurnedOff('tab-switch');
        }
      } catch (error) {
        console.warn('לא ניתן לבדוק הרשאות מיקרופון אחרי חזרה לשונית:', error);
        // גם אם לא ניתן לבדוק - נציג פופאפ כי המיקרופון כנראה נכבה
        if (this.isPaused && !this.isListening) {
          this.handleMicrophoneTurnedOff('tab-switch');
        }
      }
    } else {
      // אם אין תמיכה ב-permissions API - נציג פופאפ אם המיקרופון לא פעיל
      if (this.isPaused && !this.isListening) {
        this.handleMicrophoneTurnedOff('tab-switch');
      }
    }
  }

  /**
   * הגדרת ניטור מיקרופון - permission changes ובדיקת פעילות
   */
  private async setupMicrophoneMonitoring(): Promise<void> {
    // ניטור שינויי הרשאות מיקרופון
    if ('permissions' in navigator) {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });

        permissionStatus.onchange = () => {
          if (permissionStatus.state === 'denied' && this.isListening) {
            console.warn('🚫 Microphone permission denied - pausing recognition');
            this.handleMicrophoneTurnedOff('permission-denied');
          }
        };
      } catch (error) {
        console.warn('לא ניתן לבדוק הרשאות מיקרופון:', error);
      }
    }
  }

  /**
   * טיפול במקרה שהמיקרופון נכבה על ידי הדפדפן
   * רק מזהה את הכיבוי ומציג פופאפ - לא מכבה את המיקרופון בעצמו
   * המיקרופון כבר נכבה על ידי הדפדפן, אנחנו רק מזהה את זה ומציגים הודעה
   */
  private handleMicrophoneTurnedOff(reason: string): void {
    console.warn(`🔇 Microphone turned off by browser - reason: ${reason}`);

    // שגיאות אמיתיות של כיבוי מיקרופון על ידי הדפדפן/מערכת
    // כולל כיבוי אוטומטי במובייל ובדסקטופ על חוסר פעילות
    // כולל כיבוי כשעוברים לשונית אחרת
    const isMicrophoneError = [
      'permission-denied',
      'not-allowed',
      'service-not-allowed',
      'audio-capture',
      'mobile-auto-off',
      'desktop-inactivity',
      'tab-switch',
    ].includes(reason);

    // נציג את הפופאפ אם זו שגיאה אמיתית של כיבוי מיקרופון
    // בודקים גם אם אנחנו במצב listening או שהיינו במצב listening (למקרה שהמצב כבר השתנה)
    if (isMicrophoneError && (this.isListening || this.isStarted)) {
      // המיקרופון כבר נכבה על ידי הדפדפן, אז אנחנו רק מעדכנים את המצב ומציגים את הפופאפ
      // לא נכבה אותו בעצמנו - הוא כבר כבוי
      this.setIsListening(false);
      this.setIsPaused(true);

      // ניקוי timeouts
      if (this.restartTimeoutId) {
        clearTimeout(this.restartTimeoutId);
        this.restartTimeoutId = null;
      }

      // נציג את המודל - גם במובייל וגם בדסקטופ
      // במובייל - גם על כיבוי אוטומטי מהיר
      // בדסקטופ - על שגיאות אמיתיות וכיבוי אחרי זמן רב
      this.uiStore.setIsMicrophoneOffDialogOpen(true);
    }
  }

  /**
   * בדיקה תקופתית שהמיקרופון עדיין פעיל
   * לא מכבה את המיקרופון - רק מנטר למידע
   */
  private startMicrophoneActivityCheck(): void {
    // ניקוי בדיקה קודמת אם קיימת
    this.stopMicrophoneActivityCheck();

    this.lastRecognitionActivity = Date.now();

    // הערה: הסרנו את הלוגיקה שמכבה את המיקרופון אוטומטית
    // המיקרופון יישאר פעיל גם אם יש הפסקה בדיבור
    // רק נעדכן את זמן הפעילות האחרונה למעקב
    this.microphoneCheckInterval = setInterval(() => {
      if (!this.isListening || this.isPaused) {
        return;
      }

      // רק עדכון זמן פעילות - לא כיבוי אוטומטי
      // אם המיקרופון נכבה על ידי הדפדפן, זה יתגלה דרך onerror או onend
    }, SpeechRecognitionController.MICROPHONE_CHECK_INTERVAL);
  }

  /**
   * עצירת בדיקת פעילות מיקרופון
   */
  private stopMicrophoneActivityCheck(): void {
    if (this.microphoneCheckInterval) {
      clearInterval(this.microphoneCheckInterval);
      this.microphoneCheckInterval = null;
    }
  }

  private shouldIgnoreWord(spokenWord: string): boolean {
    return spokenWord === this.lastProcessedWord;
  }

  private shouldIgnoreErrorDueToTiming(): boolean {
    const currentTime = Date.now();
    const timeSinceLastError = currentTime - this.lastErrorTime;

    // אם לא הייתה שגיאה עדיין או אם עברו מספיק זמן - לא נתעלם
    if (this.lastErrorTime === 0 || timeSinceLastError >= SpeechRecognitionController.ERROR_DEBOUNCE_TIME) {
      return false;
    }

    // אם עברו פחות מחצי שניה מהשגיאה האחרונה - נתעלם מהשגיאה הנוכחית
    console.log(`⏰ מתעלם משגיאה - עברו רק ${timeSinceLastError}ms מהשגיאה הקודמת`);
    return true;
  }

  private recordErrorTime(): void {
    this.lastErrorTime = Date.now();
  }

  private restartListeningAfterDelay(): void {
    // ניקוי timeout קודם אם קיים
    if (this.restartTimeoutId) {
      clearTimeout(this.restartTimeoutId);
      this.restartTimeoutId = null;
    }

    this.restartTimeoutId = setTimeout(() => {
      this.restartTimeoutId = null;
      if (this.isListening && !this.isDone && !this.isPaused) {
        try {
          this.startListening(false);
        } catch (error) {
          console.error('🚫 Restart failed:', error);
          this.setIsListening(false);
          this.setIsPaused(true);
        }
      }
    }, SpeechRecognitionController.RESTART_DELAY);
  }

  // ---- פונקציות ציבוריות ----

  destroy = (): void => {
    // ניקוי timeouts למניעת memory leaks
    if (this.restartTimeoutId) {
      clearTimeout(this.restartTimeoutId);
      this.restartTimeoutId = null;
    }
    if (this.deleteRestartTimeoutId) {
      clearTimeout(this.deleteRestartTimeoutId);
      this.deleteRestartTimeoutId = null;
    }

    // ניקוי ניטור מיקרופון
    this.stopMicrophoneActivityCheck();

    document.removeEventListener('visibilitychange', this.pageVisibilityHandler);
    this.releaseMicrophone().catch((error) => {
      console.warn('Error releasing microphone on destroy:', error);
    });
  };

  /**
   * שחרור מלא של המיקרופון - סוגר את הגרף, מכבה את רצועת ההקלטה
   * ומסיר את חיווי ההקלטה של הדפדפן.
   * נקרא כשיוצאים ממצב קריאה (לא בין פסוק לפסוק ברצף).
   */
  releaseMicrophone = async (): Promise<void> => {
    this.cancelPrepare();
    this.forwardAudio = false;
    this.flushVoiceLevelLog();
    this.endStream();
    await this.closeAudioGraph();
  };

  /** סגירת גרף השמע והמיקרופון בלבד, בלי לגעת בסשן התמלול מול השרת */
  private async closeAudioGraph(): Promise<void> {
    // הגרף נלקח מהשדות לפני ההמתנה וגם השדות מתאפסים מיד: הכנה מוקדמת יכולה
    // לפתוח גרף חדש בזמן שהישן עוד נסגר, ואסור שהסגירה הישנה תמחק אותו.
    const { input, recognition, stream, audioContext } = this;
    this.input = null;
    this.recognition = null;
    this.stream = null;
    this.audioContext = null;
    try {
      input?.disconnect();
      recognition?.disconnect();
      if (recognition) {
        recognition.port.onmessage = null;
      }
      stream?.getTracks().forEach((track) => track.stop());
      if (audioContext && audioContext.state !== 'closed') {
        await audioContext.close();
      }
    } catch (error) {
      console.warn('Error while releasing microphone:', error);
    }
  }

  /**
   * פתיחה מוקדמת, כשהפסוק מוצג ועוד לפני הכפתור הירוק: המיקרופון נפתח
   * והזיהוי מתחיל להתחבר, אבל השער סגור ושום צליל לא נשלח. כך הלחיצה
   * מוצאת הכל מוכן והכיסוי מעל הפסוק יורד כמעט מיד.
   * רק כשכבר יש הרשאת מיקרופון: לא מקפיצים למשתמש בקשת הרשאה לפני שלחץ.
   * אם אף אחד לא לוחץ תוך 30 שניות, הכל נסגר.
   */
  prepareToListen = (): Promise<void> => {
    if (this.isListening || this.isInitializing || this.preparedStream || this.preparePromise) {
      return this.preparePromise ?? Promise.resolve();
    }
    if (typeof document !== 'undefined' && document.hidden) return Promise.resolve();

    const generation = ++this.prepareGeneration;
    const run = async (): Promise<void> => {
      try {
        if ((await MicrophoneService.getPermissionState()) !== 'granted') return;
        if (generation !== this.prepareGeneration) return;

        if (!this.isMicrophoneWarm) {
          await this.closeAudioGraph();
          await this.openAudioGraph();
        }
        if (generation !== this.prepareGeneration) {
          // בוטל בזמן הפתיחה (יציאה מהמסך) - לא משאירים מיקרופון פתוח
          if (!this.isListening && !this.isInitializing) await this.closeAudioGraph();
          return;
        }

        if (!this.streamActive) {
          this.beginStream();
          this.preparedStream = true;
        }
        // הקריאה שתתחיל מכאן היא הראשונה, גם אם היא תעבור במסלול החם
        this.flowPosition = 0;
        this.prepareIdleTimeoutId = setTimeout(() => {
          this.prepareIdleTimeoutId = null;
          if (!this.preparedStream || this.isListening || this.isInitializing) return;
          console.log('🎤 Prepared microphone was not used for 30 seconds; closing it');
          this.releaseMicrophone().catch((error) => {
            console.warn('Error releasing an idle prepared microphone:', error);
          });
        }, SpeechRecognitionController.PREPARE_IDLE_MS);
      } catch (error) {
        console.warn('Could not prepare the microphone ahead of time:', error);
        if (generation === this.prepareGeneration && !this.isListening && !this.isInitializing) {
          await this.closeAudioGraph().catch(() => undefined);
        }
      }
    };

    const promise = run().finally(() => {
      if (this.preparePromise === promise) this.preparePromise = null;
    });
    this.preparePromise = promise;
    return promise;
  };

  /** ביטול ההכנה המוקדמת: הטיימר נעצר וכל הכנה שעוד רצה לא תשאיר כלום פתוח */
  private cancelPrepare(): void {
    this.prepareGeneration += 1;
    this.preparedStream = false;
    if (this.prepareIdleTimeoutId) {
      clearTimeout(this.prepareIdleTimeoutId);
      this.prepareIdleTimeoutId = null;
    }
  }

  /**
   * הלחיצה לוקחת את מה שהוכן מראש. מחזיר true אם יש סשן תמלול פתוח מוכן,
   * ואז לא פותחים סשן חדש. המיקרופון עצמו נבדק בנפרד (isMicrophoneWarm).
   */
  private claimPreparedStream(): boolean {
    const claimed = this.preparedStream && this.streamActive;
    this.cancelPrepare();
    this.forwardAudio = false;
    return claimed;
  }

  recognition: AudioWorkletNode | null = null;
  audioContext: AudioContext | null = null;
  stream: MediaStream | null = null;
  input: MediaStreamAudioSourceNode | null = null;

  /** האם יש כרגע סשן תמלול פתוח מול השרת (startStream ללא stopStream) */
  private streamActive = false;

  /**
   * המיקרופון "חם" כשהגרף השמע מהקריאה הקודמת עדיין חי:
   * AudioContext פתוח, worklet קיים ורצועת המיקרופון עדיין משדרת.
   * במצב כזה אפשר להתחיל פסוק חדש בלי getUserMedia ובלי AudioContext חדש -
   * כלומר בלי ההשהיה וה"הכנה" שהמשתמש מרגיש בין פסוק לפסוק.
   */
  /** נפתח מראש כשהפסוק מוצג, לפני הלחיצה: הנורה צהובה ("להתכונן") */
  get isMicrophonePrepared(): boolean {
    return this.preparedStream;
  }

  /** המיקרופון כבר פתוח מהפסוק הקודם (זרימה רציפה) - הכיסוי מעל הפסוק יכול להיות קל */
  get hasWarmMicrophone(): boolean {
    return this.isMicrophoneWarm;
  }

  private get isMicrophoneWarm(): boolean {
    const hasLiveTrack = !!this.stream?.getAudioTracks().some((track) => track.readyState === 'live');
    return !!(this.audioContext && this.audioContext.state !== 'closed' && this.input && this.recognition && hasLiveTrack);
  }

  /** פתיחת סשן תמלול מול השרת (פעם אחת לכל קריאה) */
  private beginStream(): void {
    // זרם חדש מתחיל סגור, עד שהשרת מאשר שהזיהוי מקשיב
    this.forwardAudio = false;
    this.streamReadyPromise = new Promise<void>((resolve) => {
      this.streamReadyResolve = resolve;
    });
    // Sent per reading, not at connect: the socket opens when the app loads,
    // which can be before anyone has signed in.
    this.socket.emit('startStream', {
      token: localStorage.getItem('access_token') ?? undefined,
    });
    this.streamActive = true;
  }

  /**
   * ממתין לאישור מהשרת שהזיהוי מקשיב. מחזיר true אם האישור הגיע,
   * ו-false אם עבר הזמן הקצוב (ואז ממשיכים בכל זאת - התור בשרת שומר את השמע).
   */
  private async waitForStreamReady(): Promise<boolean> {
    if (!this.streamReadyPromise) return true;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const arrived = await Promise.race([
      this.streamReadyPromise.then(() => true),
      new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(false), SpeechRecognitionController.STREAM_READY_TIMEOUT);
      }),
    ]);
    clearTimeout(timer);

    if (this.startTiming) {
      this.startTiming.readyAt = Date.now();
      this.startTiming.readyTimedOut = !arrived;
    }
    return arrived;
  }

  /**
   * סגירת סשן התמלול מול השרת ושליחת הלוגים שנאספו.
   * חשוב לקרוא לזה בסוף כל קריאה - אחרת נשארת פתוחה עוד סשן תמלול בשרת לכל פסוק.
   */
  private endStream(): void {
    if (!this.streamActive) return;
    this.streamActive = false;
    this.socket.emit('stopStream', this.logEntries);
    // הלוגים כבר נשלחו - מאפסים כדי שלא יישלחו שוב אם הקריאה תימשך (למשל אחרי השהיה)
    this.logEntries = [];
  }

  /**
   * Stops the voice level measurement and queues its row, to go out with the
   * other logs when the stream ends. Does nothing if there is no open stream
   * or nothing was measured, so calling it twice writes one row.
   */
  private flushVoiceLevelLog(): void {
    this.voiceLevel.pause();
    const summary = this.voiceLevel.takeSummary();
    if (summary && this.streamActive) {
      this.logEntries.push(buildLogVoiceLevel(summary));
    }
  }

  /** חיבור המיקרופון לגרף השמע. השמע רץ מקומית, אבל לתמלול הוא עובר רק כשהשער פתוח */
  private connectMicrophone(): void {
    // ההמתנה לשרת ולספירה לוקחת זמן, ובינתיים הקריאה יכולה כבר להיגמר
    if (this.isDone || !this.input || !this.recognition) return;

    this.input.connect(this.recognition);
  }

  /** פתיחת השער לתמלול - מכאן והלאה מה שנאמר באמת נבדק */
  private openAudioGate(): void {
    if (this.isDone) return;

    this.forwardAudio = true;
    this.listeningStartedAt = Date.now();
    this.voiceLevel.resume(this.audioContext, this.input);

    if (this.startTiming) {
      const { startedAt, path, micReadyAt, readyAt, readyTimedOut, preopened } = this.startTiming;
      const timing = {
        path,
        flowPosition: this.flowPosition,
        micReadyMs: micReadyAt - startedAt,
        readyMs: readyAt === null ? null : readyAt - startedAt,
        readyTimedOut,
        gateOpenMs: this.listeningStartedAt - startedAt,
        mobile: SpeechRecognitionController.isMobileDevice(),
        preopened,
        operatingPoint: this.operatingPoint,
        site: window.location.hostname,
      };
      this.logEntries.push(buildLogStartTiming(timing));
      // שורה אחת בקונסול, כדי שאפשר יהיה להעתיק ולבדוק בלי לחכות ללוגים
      console.info('[start-timing]', JSON.stringify(timing));
      this.startTiming = null;
    }
  }

  /**
   * פתיחת המיקרופון וגרף השמע. השמע רץ מקומית, אבל עובר לתמלול רק כשהשער פתוח.
   * משמש גם את הפתיחה הקרה וגם את ההכנה המוקדמת.
   */
  private async openAudioGraph(): Promise<void> {
    this.audioContext = new AudioContext({ sampleRate: 16000 });
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.input = this.audioContext.createMediaStreamSource(this.stream);

    // Load the worklet module (must be served from a URL — inline blob shown below)
    const workletCode = `
      class AudioProcessor extends AudioWorkletProcessor {
        process(inputs, outputs, parameters) {
          const input = inputs[0];
          if (input && input[0]) {
            this.port.postMessage({ audioData: input[0] });
          }
          return true;
        }
      }
      registerProcessor('audio-processor', AudioProcessor);
    `;
    const blob = new Blob([workletCode], { type: 'application/javascript' });
    const workletUrl = URL.createObjectURL(blob);

    await this.audioContext.audioWorklet.addModule(workletUrl);
    URL.revokeObjectURL(workletUrl);

    // Create processor (4096 buffer size, 1 input channel, 1 output)
    this.recognition = new AudioWorkletNode(this.audioContext, 'audio-processor');

    // Receive audio data on the main thread
    this.recognition.port.onmessage = (event) => {
      // בזמן הספירה המיקרופון פועל אבל השמע לא נשלח: Speechmatics לא ישמע את הקריין
      if (!this.forwardAudio) return;

      const float32 = event.data.audioData; // Float32Array of PCM samples
      const int16 = new Int16Array(float32.length);
      for (let i = 0; i < float32.length; i++) {
        const clamped = Math.max(-1, Math.min(1, float32[i]));
        int16[i] = clamped * 0x7fff;
      }
      this.socket.emit('audioData', int16.buffer);
    };

    (this.recognition as AudioWorkletNode).connect(this.audioContext.destination);
  }

  private startRecording = async (): Promise<boolean> => {
    const startedAt = Date.now();
    try {
      // הכנה מוקדמת שעוד רצה מסתיימת קודם, כדי לא לפתוח מיקרופון פעמיים
      if (this.preparePromise) await this.preparePromise;
      const preopened = this.claimPreparedStream();

      // מסלול "מיקרופון חם" - הפסוק הקודם השאיר את המיקרופון פתוח, אז רק מחברים מחדש
      if (this.isMicrophoneWarm) {
        if (this.audioContext?.state === 'suspended') {
          await this.audioContext.resume();
        }

        // אם ה-AudioContext לא חזר לפעולה (מדיניות דפדפן) - נפתח הכל מחדש
        if (this.audioContext?.state === 'running') {
          this.logEntries.push(buildLogStartRecording(this.readingSessionStore.readingSession.textEntityId));
          if (!preopened) this.beginStream();

          // בזרימה רציפה המיקרופון כבר פתוח: מחכים רק לאישור שהזיהוי מקשיב
          this.flowPosition += 1;
          this.startTiming = { startedAt, path: 'warm', micReadyAt: Date.now(), readyAt: null, readyTimedOut: false, preopened };
          this.connectMicrophone();
          await this.waitForStreamReady();
          this.openAudioGate();
          console.log('🎤 Reusing warm microphone - no re-priming needed');
          return true;
        }
      }

      // מסלול קר - פתיחה מלאה של המיקרופון (הפעם הראשונה, או אחרי שהדפדפן כיבה אותו).
      // סשן תמלול שנפתח מראש נשאר: רק המיקרופון נפתח מחדש.
      if (preopened) {
        await this.closeAudioGraph();
      } else {
        await this.releaseMicrophone();
      }
      this.logEntries.push(buildLogStartRecording(this.readingSessionStore.readingSession.textEntityId));

      await this.openAudioGraph();

      if (!preopened) this.beginStream();

      // פתיחה קרה: המיקרופון מתחבר מיד, אבל השער לזיהוי סגור והפסוק מכוסה
      // עד שהשרת מאשר שהזיהוי מקשיב. כך שום צליל מלפני ההתחלה (רעש, דיבור
      // של מי שעוד לא התכוון לקרוא) לא מבלבל את הזיהוי.
      this.flowPosition = 1;
      this.startTiming = { startedAt, path: 'cold', micReadyAt: Date.now(), readyAt: null, readyTimedOut: false, preopened };
      this.connectMicrophone();

      await this.waitForStreamReady();

      // הזיהוי מקשיב: הכיסוי יורד והשמע מתחיל לעבור באותו רגע
      this.openAudioGate();

      return false;
    } catch (err) {
      console.error('Error accessing microphone:', err);
      return false;
    }
  };

  initRecognition = async (): Promise<void> => {
    const generation = ++this.recognitionGeneration;

    MicrophoneService.checkMicrophonePermission();

    // startRecording כבר מחכה לסשן התמלול בשרת (ולספירה, בפתיחה קרה),
    // ולכן כשהוא חוזר המיקרופון באמת מקליט ואפשר להתחיל לקרוא.
    await this.startRecording();

    // בינתיים התחילה קריאה אחרת, או שהקריאה הזאת נגמרה - לא דורסים את המצב הנוכחי
    if (generation !== this.recognitionGeneration || !this.isStarted || this.isDone) {
      return;
    }

    this.setIsInitializing(false);
    this.setIsListening(true);
  };

  handleRecognitionStart = (): void => {
    console.log('🎤 Recognition started');
    // עדכון זמן פעילות אחרונה
    this.lastRecognitionActivity = Date.now();

    // במובייל - שמירת זמן התחלה לזיהוי כיבוי מהיר
    if (SpeechRecognitionController.isMobileDevice()) {
      this.recognitionStartTime = Date.now();
    }
  };

  handleRecognitionEnd = (): void => {
    console.log('🎤 handleRecognitionEnd called', {
      isListening: this.isListening,
      isDone: this.isDone,
      isPaused: this.isPaused,
      isPerformingDeleteOperation: this.isPerformingDeleteOperation,
      timestamp: new Date().toLocaleTimeString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n'),
    });

    // אם אנחנו במהלך פעולת מחיקה - לא נתחיל מחדש
    if (this.isPerformingDeleteOperation) {
      console.log('🚫 Not restarting - delete operation in progress');
      return;
    }

    // אם זה נקרא בזמן שאנחנו במצב שמיעה, נבדוק אם זה כיבוי אוטומטי
    if (this.isListening && !this.isDone && !this.isPaused) {
      const timeSinceLastActivity = Date.now() - this.lastRecognitionActivity;
      const timeSinceStart = this.recognitionStartTime > 0 ? Date.now() - this.recognitionStartTime : 0;

      // במובייל - בדיקה מיוחדת לכיבוי מהיר
      if (SpeechRecognitionController.isMobileDevice()) {
        // אם recognition נגמר מהר מאוד (פחות מ-1.5 שניות מההתחלה) - זה כנראה כיבוי אוטומטי
        if (
          timeSinceStart > 0 &&
          timeSinceStart < SpeechRecognitionController.MOBILE_QUICK_OFF_THRESHOLD &&
          timeSinceLastActivity < SpeechRecognitionController.MOBILE_QUICK_OFF_THRESHOLD
        ) {
          console.warn('🔇 Mobile browser quickly turned off microphone - likely auto-off due to no speech');
          this.handleMicrophoneTurnedOff('mobile-auto-off');
          return;
        }

        // בדיקה רגילה - אם עברו יותר מ-2 שניות ללא פעילות
        if (timeSinceLastActivity > SpeechRecognitionController.MOBILE_AUTO_OFF_THRESHOLD) {
          console.warn('🔇 Mobile browser auto-turned off microphone - recognition ended due to inactivity');
          this.handleMicrophoneTurnedOff('mobile-auto-off');
          return;
        }
      } else {
        // בדסקטופ - threshold ארוך יותר (10 שניות)
        if (timeSinceLastActivity > 10000) {
          console.warn('🔇 Desktop browser auto-turned off microphone - recognition ended due to inactivity');
          this.handleMicrophoneTurnedOff('desktop-inactivity');
          return;
        }
      }

      // אם זה לא נראה כמו כיבוי אוטומטי - נמשיך לנסות
      console.log('🔄 Attempting to restart listening...');
      this.restartListeningAfterDelay();
    } else {
      // אם ההכרה נגמרה אבל אנחנו לא במצב listening - המיקרופון כנראה נכבה
      if (!this.isPaused && this.isStarted && !this.isDone) {
        console.warn('⚠️ Recognition ended unexpectedly - microphone may be off');
        // לא נשנה את המצב אוטומטית כאן - נחכה לבדיקה התקופתית
      }

      console.log('❌ Not restarting - conditions not met', {
        isListening: this.isListening,
        isDone: this.isDone,
        isPaused: this.isPaused,
      });
    }
  };

  // Stub to satisfy ISpeechRecognitionStore interface (actual scoring is incremental)
  processWordRecognition = (_correctWords: string[], _receivedWords: string[], _candidateTolerance: number, _sPos: number): number => 0;

  /**
   * Applies one final segment's alignment result to the reading session.
   * Only processes correctWords[cursor .. cursor + scores.length).
   */
  private applySegmentToSession(
    correctWords: string[],
    cursor: number,
    result: IncrementalAlignResult,
  ): void {
    const { scores, candidate_words } = result;
    const segLen = scores.length;

    this.logEntries.push(
      buildLogBatchRecognitionStart(correctWords, candidate_words.filter(Boolean), 0, cursor),
    );

    // Verdict per matched word from its edit count; unmatched positions are
    // Missing, and trailing unmatched positions within the segment stay
    // Pending because the next segment may still bring them.
    const statuses: WordStatus[] = result.edits.map((e) => {
      if (e === null) return WordStatus.Missing;
      const verdict = verdictFor(e);
      if (verdict === 'correct') return WordStatus.Correct;
      if (verdict === 'near') return WordStatus.Near;
      return WordStatus.Mistake;
    });
    for (let j = statuses.length - 1; j >= 0; j--) {
      if (statuses[j] !== WordStatus.Missing) break;
      statuses[j] = WordStatus.Pending;
    }

    // ריווח בין הדבקים: a word standing alone between two read words, with
    // enough unaccounted time around it for the reader to have said it, was
    // swallowed into a neighbour rather than skipped. It counts as read and
    // is shown amber, so the child is told to separate the words, not to
    // read them again.
    const read = (st: WordStatus) =>
      st === WordStatus.Correct || st === WordStatus.Near || st === WordStatus.Coarticulated;
    const pace = readerPace(this.paceSamples);
    const timingAt = (j: number): WordTiming | null =>
      j >= 0 ? result.wordTimes[j] ?? null : this.wordTimings[cursor + j] ?? null;
    const readAt = (j: number): boolean =>
      j >= 0 ? read(statuses[j]) : this.wordTimings[cursor + j] != null;
    for (let j = 0; j < segLen; j++) {
      if (statuses[j] !== WordStatus.Missing) continue;
      if (!readAt(j - 1) || j + 1 >= segLen || !read(statuses[j + 1])) continue;
      const swallowed = isCoarticulated({
        skipped: correctWords[cursor + j],
        previous: { word: correctWords[cursor + j - 1], timing: timingAt(j - 1) },
        next: { word: correctWords[cursor + j + 1], timing: timingAt(j + 1) },
        afterNext:
          j + 2 < segLen && read(statuses[j + 2])
            ? { word: correctWords[cursor + j + 2], timing: timingAt(j + 2) }
            : undefined,
        pace,
      });
      if (swallowed) {
        statuses[j] = WordStatus.Coarticulated;
        // A positive score, so the done condition and the per-word log both
        // see a word that was read.
        scores[j] = 0.5;
      }
    }

    for (let j = 0; j < segLen; j++) {
      const wordIndex = cursor + j;
      const status = statuses[j];
      const timing = result.wordTimes?.[j];
      this.logEntries.push(buildLogEvalWord(
        correctWords[wordIndex],
        candidate_words[j],
        scores[j],
        status,
        timing?.start_time,
        timing?.end_time,
        timing?.confidence,
      ));
      if (status !== WordStatus.Pending) {
        this.wordTimings[wordIndex] = timing ?? null;
        if (status === WordStatus.Correct && timing) {
          this.paceSamples.push({ word: correctWords[wordIndex], timing });
        }
        (this.readingSessionStore as ReadingSessionStore).updateWordStatus(
          wordIndex, correctWords[wordIndex], status,
        );
      }
    }
  }

  private clearTailIdle(): void {
    if (this.tailIdleTimeoutId) {
      clearTimeout(this.tailIdleTimeoutId);
      this.tailIdleTimeoutId = null;
    }
    this.tailIdleWords = null;
  }

  /** (Re)starts the quiet-tail clock. Any final or partial restarts it; only silence lets it fire. */
  private armTailIdle(correctWords: string[]): void {
    this.clearTailIdle();
    this.tailIdleWords = correctWords;
    this.tailIdleTimeoutId = setTimeout(() => {
      this.tailIdleTimeoutId = null;
      this.tailIdleWords = null;
      if (!this.isListening || this.isDone) return;
      console.log('⏱️ The tail of the reading went quiet; closing it as it stands');
      this.logEntries.push(buildLogEndConditionStats(
        (this.readingSessionStore as ReadingSessionStore).stats.correctWords,
        (this.readingSessionStore as ReadingSessionStore).stats.failedWords,
        (this.readingSessionStore as ReadingSessionStore).stats.pendingWords,
        (this.readingSessionStore as ReadingSessionStore).stats.accuracyPercentage,
      ));
      this.doneListening();
    }, SpeechRecognitionController.TAIL_IDLE_MS);
  }

  /**
   * Emits stopStream and calls doneListening when the last correct word is
   * scored. When the reader has reached the tail (at most one word unscored,
   * or every word scored but the last one wrong) and nothing more arrives
   * for a while, the reading is closed as it stands.
   */
  private checkDoneCondition(correctWords: string[]): void {
    this.clearTailIdle();
    const atTail = this.wordScores.length >= correctWords.length - 1;
    const lastScored = this.wordScores.length >= correctWords.length && this.wordScores[correctWords.length - 1] > 0;
    if (!lastScored) {
      if (atTail && correctWords.length > 0) {
        this.armTailIdle(correctWords);
      }
      return;
    }

    this.logEntries.push(buildLogEndConditionStats(
      (this.readingSessionStore as ReadingSessionStore).stats.correctWords,
      (this.readingSessionStore as ReadingSessionStore).stats.failedWords,
      (this.readingSessionStore as ReadingSessionStore).stats.pendingWords,
      (this.readingSessionStore as ReadingSessionStore).stats.accuracyPercentage,
    ));
    this.doneListening();
  }

  startListening = (toClearData?: boolean): void => {
    try {
      // גם אתחול שעדיין רץ נחשב "כבר מתחילים", אחרת נפתח סשן תמלול כפול
      if (this.isListening || this.isInitializing) return;

      if (toClearData) {
        this.clearData();

        // יצירת ReadingSession חדש ממקור הקריאה (ספר או סידור)
        const source = this.readingSource;
        if (source) {
          this.readingSessionStore.initializeFromTextEntity(source);
        }
      }

      this.setIsInitializing(true);
      // console.log('🎤 setIsInitializing(true) called, current value:', this.isInitializing);

      this.setIsStarted(true);
      this.initRecognition();

      // התחלת בדיקת פעילות מיקרופון
      this.startMicrophoneActivityCheck();

      // 📊 Start reading session logging
      readingSessionLogger.startSession();
    } catch (e) {
      console.warn('🚨 Start error:', e);
      this.setIsListening(false);

      // Log session as crashed if it was started
      if (readingSessionLogger.isSessionActive()) {
        this.logSessionEnd('crashed', 'system_error');
      }
    }
  };

  stopListening = (): void => {
    // עצירת בדיקת פעילות מיקרופון
    this.clearTailIdle();

    this.flushVoiceLevelLog();
    this.logEntries.push(buildLogEndSession());

    this.stopMicrophoneActivityCheck();

    if (this.recognition) {
      this.setIsListening(false);
      this.recognitionAbort();
    } else {
      this.setIsListening(false);
    }

    // סוגרים את סשן התמלול בשרת ושולחים את הלוגים. המיקרופון עצמו נשאר פתוח
    // כדי שהפסוק הבא יתחיל מיד - שחרור מלא נעשה ב-releaseMicrophone.
    this.endStream();

    // 📊 Log session end if active (user left)
    if (readingSessionLogger.isSessionActive() && !this.isDone) {
      this.logSessionEnd('abandoned', 'user_left');
    }
  };

  toggleListening = (): void => {
    this.setIsPaused(!this.isPaused);

    if (this.isPaused) {
      if (this.isListening) {
        this.stopListening();
        // הערה: allSpeechTexts הוא legacy - לא בשימוש פעיל, אבל נשמר לתאימות עם interface
        // this.setAllSpeechTexts(`${this.allSpeechTexts} ${this.speechText}`);
      }
    } else {
      this.startListening(false);
    }
  };

  doneListening = (): void => {
    // חשוב: לסמן isDone לפני stopListening. אחרת stopListening ירשום את הסשן
    // בטעות כ-'abandoned' (ה-guard שלו הוא `!this.isDone`), ותיווצר רשומה כפולה
    // "ננטש" לצד ה-"הושלם" עבור אותה קריאה.
    this.setIsDone(true);
    this.stopListening();
    this.setScore(this.accuracyScore);

    // 📊 Log session end (completed successfully)
    if (readingSessionLogger.isSessionActive()) {
      this.logSessionEnd('completed', 'user_finished');
    }

    this.uiStore.setIsDoneListeningDialogOpen(true);
  };

  clearData = (): void => {
    // ניקוי timeouts לפני איפוס
    if (this.restartTimeoutId) {
      clearTimeout(this.restartTimeoutId);
      this.restartTimeoutId = null;
    }
    if (this.deleteRestartTimeoutId) {
      clearTimeout(this.deleteRestartTimeoutId);
      this.deleteRestartTimeoutId = null;
    }

    // ניקוי ניטור מיקרופון
    this.stopMicrophoneActivityCheck();

    // 📊 Log session end if active (data cleared)
    if (readingSessionLogger.isSessionActive()) {
      this.logSessionEnd('abandoned', 'user_left');
    }

    this.stopListening();
    this.voiceLevel.resetReading();
    this.setSpeechText('');
    this.setAllSpeechTexts('');
    this.setIsListening(false);
    this.setIsDone(false);
    this.setIsPaused(false);
    this.setIsStarted(false);
    this.setIsInitializing(false);
    this.lastProcessedWord = '';

    // איפוס זמן השגיאה האחרונה
    this.lastErrorTime = 0;

    // איפוס זמן פעילות אחרונה
    this.lastRecognitionActivity = 0;
    this.listeningStartedAt = 0;

    // איפוס זמן התחלת recognition
    this.recognitionStartTime = 0;

    // איפוס דגל פעולת המחיקה
    this.isPerformingDeleteOperation = false;

    this.clearTailIdle();
    this.transcribedWords = [];
    this.wordScores = [];
    this.wordTimings = [];
    this.paceSamples = [];
    this.logEntries = [];
    this.alignCursor = 0;
    this.partialText = '';
    this.correctWordsPromise = null
  };

  /**
   * Helper function to log session end with current stats
   */
  private logSessionEnd(
    status: 'completed' | 'abandoned' | 'crashed',
    reason: 'user_finished' | 'user_left' | 'system_error' | 'timeout',
  ): void {
    if (!this.readingSessionStore.hasActiveSession) {
      return;
    }

    const stats = this.readingSessionStore.stats;
    const readingSession = this.readingSessionStore.readingSession;

    // Get book context if available
    const source: any = this.readingSource;
    const context = {
      bookId: source?.book_id,
      textEntityId: source?.id,
    };

    readingSessionLogger.endSession(
      {
        totalWords: stats.correctWords + stats.failedWords + stats.pendingWords,
        correctWords: stats.correctWords,
        errorWords: stats.failedWords,
        accuracyPercentage: stats.accuracyPercentage,
      },
      status,
      reason,
      context,
    );
  }

  // ---- Setters (שמירת תאימות עם הממשק) ----

  setIsListening = (isListening: boolean): void => {
    this.isListening = isListening;
  };

  setSpeechText = (speechText: string): void => {
    this.speechText = speechText;
  };

  setAllSpeechTexts = (allSpeechTexts: string): void => {
    this.allSpeechTexts = allSpeechTexts;
  };

  setIsStarted = (isStarted: boolean): void => {
    this.isStarted = isStarted;
  };

  setIsInitializing = (isInitializing: boolean): void => {
    this.isInitializing = isInitializing;
  };

  setIsPaused = (isPaused: boolean): void => {
    this.isPaused = isPaused;
  };

  setIsDone = (isDone: boolean): void => {
    this.isDone = isDone;
  };

  setScore = (score: number): void => {
    this.score = score;
  };

  setReadingResults = (readingResults: any): void => {
    this.readingResults = readingResults;
  };

  // ---- מטודות עבור ActionButtons ----

  /**
   * Helper method משותף למחיקת מילה/כל הטקסט - מפחית קוד כפול
   */
  private performDeleteOperation(deleteFn: () => void, operationName: string): void {
    console.log(`🗑️ ${operationName} called`, {
      isListening: this.isListening,
      isPaused: this.isPaused,
      speechText: this.speechText,
    });

    // שמירת המצב הנוכחי לפני המחיקה
    const wasListening = this.isListening;
    const wasPaused = this.isPaused;

    // סימון שאנחנו במהלך פעולת מחיקה
    this.isPerformingDeleteOperation = true;

    // עצירה זמנית של הזיהוי אם פועל
    if (wasListening && !wasPaused) {
      this.recognitionAbort();
      this.setIsListening(false);
    }

    // ביצוע המחיקה
    deleteFn();

    // עדכון speechText לתאימות לאחור
    this.speechText = this.readingSessionStore.spokenText;

    // החזרת המצב אם היינו בהקשבה
    if (wasListening && !wasPaused) {
      // ניקוי timeout קודם אם קיים
      if (this.deleteRestartTimeoutId) {
        clearTimeout(this.deleteRestartTimeoutId);
        this.deleteRestartTimeoutId = null;
      }

      this.deleteRestartTimeoutId = setTimeout(() => {
        this.deleteRestartTimeoutId = null;
        this.isPerformingDeleteOperation = false;
        this.setIsListening(true);
        this.setIsPaused(false); // וידוא שאנחנו לא במצב השהיה
        this.initRecognition();

        console.log(`🔄 Restarted listening after ${operationName}`, {
          isListening: true,
          isPaused: false,
        });
      }, SpeechRecognitionController.DELETE_RESTART_DELAY);
    } else {
      this.isPerformingDeleteOperation = false;
    }

    console.log(`🗑️ ${operationName} finished`, {
      isListening: this.isListening,
      isPaused: this.isPaused,
      speechText: this.speechText,
    });
  }

  removeLastWord = (): void => {
    this.performDeleteOperation(() => this.readingSessionStore.removeLastWord(), 'removeLastWord');
  };

  removeAllText = (): void => {
    this.performDeleteOperation(() => this.readingSessionStore.resetSession(), 'removeAllText');
  };

  // ---- מטודות לתאימות עם הממשק (ריקות כי לא בשימוש) ----

  calculateAndSendRank = async (): Promise<void> => {
    // הפונקציה הזו נקראת ב-ScoreAndResultModal
    // העדכון של ה-rank כבר נעשה ב-saveUserScore, אז אין צורך לעשות כלום כאן
    // הפונקציה נשמרת לתאימות עם הממשק אבל לא מבצעת פעולה
    console.log('ℹ️ calculateAndSendRank נקרא - העדכון כבר נעשה ב-saveUserScore');
  };
}

export default SpeechRecognitionController; 
